/**
 * Central section management API. Handles add, remove, move, duplicate,
 * swap, and template loading. All operations go through the state store
 * for undo/redo support and auto-save.
 */

import { verifyEditableIdRepairs } from '../shared/editable-id-repair.js';
import { canonicalizeAssetBackedMediaHTML } from '../shared/asset-html.js';
import { sha256Hex } from '../shared/site-deploy.js';
import { store } from '../core/state.js';
import { templateDeclaresSmoothScroll } from '../shared/site-effects.js';
import { eventBus } from '../core/events.js';
import {
  isSectionLockedByPeer,
  notifyLockedDenied,
  lockKeyForInstance,
  lockKeyForShared
} from '../editing/collab-guard.js';
import { extractContent, applyContent, assessSwapRisk } from './variant-swap.js';
import {
  buildBulkDeletePatches,
  buildBulkDuplicatePatches,
  buildMovePatchesForOrder,
  resolveAscending,
  targetOrderForGap,
  targetOrderForNudge
} from './bulk-operations.js';
import { inferCategory } from '../shared/category-map.js';
import { formatSectionName } from '../shared/section-display-name.js';
import {
  isUnknownCategory,
  shouldUseTemplateSectionThumbnail
} from '../shared/section-categories.js';
import { stampEditableIds } from '../editing/editable-utils.js';
import {
  mergeMobileMenu,
  revertMobileMenu,
  createCssomCssEngine,
  pruneOverridesForMerge,
  MOBILE_MENU_LABELS
} from './mobile-menu-merge.js';
import { ELIGIBILITY_REASON_COPY } from '../shared/mobile-menu-contract.js';
import { queueHint } from '../ui/feedback.js';
import { CLIENT_LINK_MARKER_PREFIX } from '../shared/client-info.js';
import { createFreeformSection } from './freeform-section.js';
import { bridge } from '../editing/bridge.js';
import { extractTemplateFontChoices } from '../shared/font-utils.js';
import { applyCompositionSkin } from '../shared/composition-skin.js';
import { loadJSON } from '../core/asset-loader.js';
import { catalogSectionThumbnail, templateSectionThumbnail } from '../shared/thumbnail-url.js';
import { loadTemplateSupportCSS } from '../shared/template-support-css.js';
import {
  normalizeSourceTemplateDocument,
  normalizeSourceTemplateSectionPlacement
} from '../shared/source-template-document.js';
import { deriveTemplateLoadTheme } from '../shared/theme-tokens.js';
import { deriveClientInfoFromTemplate } from '../shared/template-client-info.js';
import { clientInfoToSourceProfile } from '../shared/client-info-migrate.js';
import {
  isSharedCategory,
  isSharedRole,
  sharedSectionStorePath,
  sharedSectionLabel
} from '../shared/shared-section-types.js';
import { getUniquePageName, generatePageId } from '../pages/page-name-utils.js';
import { extractButtonStyles, detectTemplateButtonEffect } from '../shared/button-style-extract.js';
import { sectionSourceHash } from '../shared/section-source-hash.js';
import { computeIdProvenance } from '../shared/id-provenance.js';
import { registerPageHydration } from '../core/save-load.js';

// CE-5: save-load reaches page hydration through this registration rather
// than a static import, so the guest surfaces can read bundles without
// bundling the structure-op graph. The builder shell always loads this
// module, so registration always precedes any save that needs it. Function
// declarations hoist — the references below are the real exports.
registerPageHydration({ ensureAllPagesHydrated, stopBackgroundPrefetch });

let renderPreviewFn = null;
let showToastFn = null;
let showUndoToastFn = null;
let confirmFn = null;
let promptSwapChoiceFn = null;
let sectionIndex = null;
/** Per-category session memory for swap prompt choice. Resets on page reload. */
const _swapPreference = new Map();

/**
 * Module-scoped cache of section JSON promises, shared across loadTemplate +
 * hydratePage calls so a section referenced by multiple pages is fetched once.
 * Key: `${siteName}::${sectionId}`. Transient; never serialized.
 */
const _sectionFetchCache = new Map();
const _templateSourceProfileCache = new Map();
let _composableTemplatesPromise = null;

/**
 * In-flight hydration promises, keyed by pageId. Lets foreground hydratePage
 * (from setActivePage) and background prefetch share a single fetch when both
 * target the same page.
 */
const _inFlightHydrations = new Map();

/**
 * Monotonic counter incremented at the start of every loadTemplate call.
 * Captured by hydratePage / prefetch loops at entry and re-checked before
 * writing — guards against undo, project reload, or a new template loading
 * over the top of in-flight fetches.
 */
let _loadGeneration = 0;

/** Background prefetch cancellation token. */
let _prefetchAbort = null;

const TEMPLATE_BUTTON_TEXT_WRAPPER_DOMAINS = new Set([
  'architecturalphotographyinc.com',
  'ariseconstructions.com',
  'bladeworksstudio.com',
  'bluffcitywebdesigns.com',
  'jmbikeshop.com',
  'kingsbarbercollege.com',
  'learntoskatehb.com',
  'madscientistdemo.netlify.app',
  'makopoolrestorations.com',
  'oui.digital',
  'piedmontrvcenter.com',
  'shouldrs.com',
  'staleyequipment.com',
  'yourherocare.com'
]);

function emitAsyncStatus(loading, operation, detail = {}) {
  eventBus.emit('section:async-status', { loading, operation, ...detail });
}

export function init({ renderPreview, showToast, showUndoToast, confirmDelete, promptSwapChoice }) {
  renderPreviewFn = renderPreview;
  showToastFn = showToast;
  showUndoToastFn = showUndoToast || null;
  confirmFn = confirmDelete || null;
  promptSwapChoiceFn = promptSwapChoice || null;
}

function generateInstanceId() {
  return 'inst-' + crypto.randomUUID().slice(0, 8);
}

function getPagePath(pageId) {
  const state = store.getState();
  const page = pageId ? state.pages.find((p) => p.id === pageId) : store.getActivePage();
  if (!page) return null;
  const pageIndex = state.pages.findIndex((p) => p.id === page.id);
  if (pageIndex === -1) return null;
  return { page, pageIndex, sectionsPath: `pages.${pageIndex}.sections` };
}

function findSectionIndex(page, instanceId) {
  return page.sections.findIndex((s) => s.instanceId === instanceId);
}

/**
 * BC-1. Re-find a page section by IDENTITY after an await, immediately before
 * the write. swap/reset used to resolve an array index up front and write to
 * that index after loading data and prompting the user, so a reorder, insert
 * or delete landing in between (the user in another surface, or a peer)
 * silently overwrote a DIFFERENT section.
 *
 * `captured` is the section object the operation read before awaiting. If the
 * section's own content changed meanwhile, the merge the caller computed from
 * `captured` would discard that change, so that is refused too.
 *
 * @returns {{ok: true, ctx: object, idx: number} | {ok: false, reason: 'gone'|'changed'}}
 */
function reresolvePageSection(pageId, instanceId, captured) {
  const ctx = getPagePath(pageId);
  const idx = ctx ? findSectionIndex(ctx.page, instanceId) : -1;
  if (idx === -1) return { ok: false, reason: 'gone' };
  const live = ctx.page.sections[idx];
  if (
    live !== captured &&
    (live.html !== captured.html ||
      JSON.stringify(live.overrides ?? null) !== JSON.stringify(captured.overrides ?? null))
  ) {
    return { ok: false, reason: 'changed' };
  }
  return { ok: true, ctx, idx };
}

function announceStaleTarget(reason, verb) {
  if (!showToastFn) return;
  showToastFn(
    reason === 'gone'
      ? `That section was removed before the ${verb} finished. Nothing was changed.`
      : `That section changed before the ${verb} finished. Nothing was changed — try again.`,
    'info'
  );
}

// BC-14: the shared formatter, so an undo toast names a section the way the
// list beside it does ("Deleted Hero", never "Deleted stitch-1033"). The old
// local tail ranked catalog ids above the category; since BC-8 there is no
// destructive confirm left for that to disambiguate — Undo is the confirmation.
function getSectionDisplayLabel(section) {
  return formatSectionName(section);
}

// BC-5: the undo toast after a replacement names the section the way every
// other surface does — "Replaced Navigation", never "Swapped to stitch-2440".
function replacedLabel(instance, mismatches) {
  const name = formatSectionName(instance);
  return mismatches > 0
    ? `Replaced ${name} (${mismatches} field${mismatches > 1 ? 's' : ''} could not be mapped)`
    : `Replaced ${name}`;
}

/**
 * Extract :root CSS variable declarations from raw template CSS text.
 * Used to recover dark-mode tokens when a template stores them in dark.css.
 */
function extractRootThemeVars(cssText) {
  const vars = {};
  if (typeof cssText !== 'string' || cssText.trim() === '') return vars;

  const rootBlockRe = /:root\s*\{([\s\S]*?)\}/g;
  let rootMatch;
  while ((rootMatch = rootBlockRe.exec(cssText)) !== null) {
    const block = rootMatch[1] || '';
    const varRe = /(--[\w-]+)\s*:\s*([^;{}]+)\s*;/g;
    let varMatch;
    while ((varMatch = varRe.exec(block)) !== null) {
      vars[varMatch[1]] = varMatch[2].trim();
    }
  }

  return vars;
}

function applyTemplateFonts(template, themeVars = null) {
  const fonts = extractTemplateFontChoices(template, themeVars);
  store.update('font', fonts.bodyFont || 'Roboto', null);
  store.update('headingFont', fonts.headingFont || null, null);
  store.update('headingFontWeight', null, null);
  // DERIVED, not chosen. Until someone picks a font, the theme is only a
  // readout of the template's own dominant face, and section CSS must not be
  // restyled to it — that is what replaced this template's Slackey and Rock
  // Salt headings with its own Raleway. See `fontSource` in core/state.js.
  store.update('fontSource', 'template', null);
  return fonts;
}

/**
 * A bare URL string, deliberately — the confirm dialog takes `contextThumbnailSrc`
 * and has nowhere to put a srcset or a fallback ladder. It therefore resolves to
 * the SOURCE image, which exists whether or not the WebP derivative pass has run.
 * One small thumbnail in a modal is not the surface where bytes matter; the grids
 * that render dozens at once use the descriptors instead.
 */
async function getSectionConfirmThumbnailSrc(section) {
  if (!section) return '';

  // Template-origin variants: use template thumbnail directory first.
  if (
    section.templateSiteName &&
    section.templateSectionId &&
    shouldUseTemplateSectionThumbnail(section)
  ) {
    return (
      templateSectionThumbnail(section.templateSiteName, section.templateSectionId)?.source || ''
    );
  }

  // Legacy template sections may only carry catalogKey + sectionId.
  if (
    !section.templateSiteName &&
    section.catalogKey?.startsWith('template-') &&
    section.sectionId &&
    String(section.category || '')
      .trim()
      .toLowerCase() !== 'unknown'
  ) {
    const siteName = section.catalogKey.slice('template-'.length, -(section.sectionId.length + 1));
    return templateSectionThumbnail(siteName, section.sectionId)?.source || '';
  }

  // Standard CodeStitch sections resolve from section-index metadata.
  if (!section.catalogKey) return '';
  const index = await getSectionIndex();
  const match = index.find(
    (entry) =>
      entry?.catalogKey === section.catalogKey &&
      (!section.category || entry?.category === section.category)
  );
  if (!match?.thumbnail) return '';
  return catalogSectionThumbnail(match.thumbnail)?.source || '';
}

async function fetchSectionDataFile(catalogKey, category) {
  const logicalPath = `data/sections/${category}/${catalogKey}.json`;
  const data = await loadJSON(logicalPath);
  // Provenance contract (docs/fleet-contracts.md §1): a section's identity is the
  // logical path that was actually fetched, stamped here by the loader. Duplicate
  // (templateSiteName, catalogKey) pairs exist with different content, so this
  // cannot be reconstructed later. Mutating the shared cache entry is safe: the
  // value is a pure function of the path.
  if (data && typeof data === 'object') {
    data.provenancePath = logicalPath;
    data.sourceHash = await sectionSourceHash(data);
  }
  return data;
}

function extractTemplateSectionIdFromCatalogKey(catalogKey, index) {
  if (!catalogKey || typeof catalogKey !== 'string' || !catalogKey.startsWith('template-'))
    return null;

  // template keys follow template-<siteName>-<templateSectionId>, but both site
  // and sectionId can contain dashes. Resolve by matching known site prefixes.
  let resolvedSectionId = null;
  let bestPrefixLength = -1;
  for (const entry of index) {
    const site = typeof entry?.templateSiteName === 'string' ? entry.templateSiteName.trim() : '';
    if (!site) continue;
    const prefix = `template-${site}-`;
    if (catalogKey.startsWith(prefix) && prefix.length > bestPrefixLength) {
      bestPrefixLength = prefix.length;
      resolvedSectionId = catalogKey.slice(prefix.length);
    }
  }
  return resolvedSectionId || null;
}

async function loadFallbackTemplateSectionData(catalogKey, category, indexOverride = null) {
  if (!catalogKey || typeof catalogKey !== 'string' || !catalogKey.startsWith('template-'))
    return null;

  const index = Array.isArray(indexOverride) ? indexOverride : await getSectionIndex();
  if (!Array.isArray(index) || index.length === 0) return null;
  const templateSectionId = extractTemplateSectionIdFromCatalogKey(catalogKey, index);
  if (!templateSectionId) return null;

  const fallbackMeta = index.find(
    (entry) =>
      entry?.category === category &&
      entry?.sectionId === templateSectionId &&
      typeof entry.catalogKey === 'string' &&
      !entry.catalogKey.startsWith('template-')
  );
  if (!fallbackMeta) return null;

  try {
    const fallback = await fetchSectionDataFile(fallbackMeta.catalogKey, category);
    console.warn(
      `[SectionManager] Missing template section "${catalogKey}" in ${category}; ` +
        `falling back to "${fallbackMeta.catalogKey}".`
    );
    return fallback;
  } catch (err) {
    console.warn('[SectionManager] Template fallback also failed:', err);
    return null;
  }
}

export async function loadSectionData(catalogKey, category) {
  if (catalogKey && typeof catalogKey === 'string' && catalogKey.startsWith('template-')) {
    const index = await getSectionIndex();
    const hasExactEntry = index.some(
      (entry) => entry?.catalogKey === catalogKey && entry?.category === category
    );
    if (!hasExactEntry) {
      const fallback = await loadFallbackTemplateSectionData(catalogKey, category, index);
      if (fallback) return fallback;
    }
  }

  try {
    return await fetchSectionDataFile(catalogKey, category);
  } catch (err) {
    const fallback = await loadFallbackTemplateSectionData(catalogKey, category);
    if (fallback) return fallback;
    console.error(`[SectionManager] Failed to load section ${catalogKey}:`, err);
    if (showToastFn) showToastFn(`Failed to load section: ${catalogKey}`, 'error');
    return null;
  }
}

async function loadTemplateSectionData(siteName, sectionId) {
  const logicalPath = `data/templates/${siteName}/${sectionId}.json`;
  try {
    const data = await loadJSON(logicalPath);
    // Provenance contract (docs/fleet-contracts.md §1) — see fetchSectionDataFile.
    if (data && typeof data === 'object') {
      data.provenancePath = logicalPath;
      data.sourceHash = await sectionSourceHash(data);
    }
    return data;
  } catch {
    return null;
  }
}

async function getComposableTemplatesMap() {
  if (!_composableTemplatesPromise) {
    _composableTemplatesPromise = loadJSON('data/composable-templates.json').catch(() => ({}));
  }
  return _composableTemplatesPromise;
}

async function resolveTemplateBundleId(siteName, template) {
  if (template?.bundleId) return template.bundleId;
  const map = await getComposableTemplatesMap();
  const entry = map && typeof map === 'object' ? map[siteName] : null;
  return typeof entry?.bundleId === 'string' && entry.bundleId.trim() ? entry.bundleId.trim() : '';
}

async function loadTemplateSourceProfile(siteName, template) {
  const bundleId = await resolveTemplateBundleId(siteName, template);
  if (!bundleId) return {};
  if (_templateSourceProfileCache.has(bundleId)) {
    return _templateSourceProfileCache.get(bundleId);
  }
  const promise = loadJSON(`data/template-bundles/${bundleId}/source-profile.json`).catch(
    () => ({})
  );
  _templateSourceProfileCache.set(bundleId, promise);
  return promise;
}

function collectAllCurrentSections(state) {
  const sections = [];
  if (state?.sharedSections?.navigation) sections.push(state.sharedSections.navigation);
  if (Array.isArray(state?.pages)) {
    for (const page of state.pages) {
      if (Array.isArray(page?.sections)) sections.push(...page.sections);
    }
  }
  if (state?.sharedSections?.footer) sections.push(state.sharedSections.footer);
  return sections;
}

function normalizeTemplateDomain(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function inferButtonTextWrapperClassFromSourceDomain(state) {
  const sourceDomain = normalizeTemplateDomain(state?.sourceTemplateDomain);
  if (sourceDomain && TEMPLATE_BUTTON_TEXT_WRAPPER_DOMAINS.has(sourceDomain)) {
    return 'cs-button-text';
  }
  return '';
}

function findCurrentButtonTextWrapperClass(state) {
  if (typeof DOMParser === 'undefined') return '';
  const parser = new DOMParser();
  for (const section of collectAllCurrentSections(state)) {
    const html = String(section?.html || '');
    if (!html.trim()) continue;
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) continue;
    const buttons = root.querySelectorAll(
      'a.cs-button-solid, button.cs-button-solid, a.cs-button-transparent, button.cs-button-transparent'
    );
    for (const button of buttons) {
      const textWrapper = Array.from(button.children || []).find((child) =>
        child.classList?.contains('cs-button-text')
      );
      if (textWrapper) return 'cs-button-text';
    }
  }
  return inferButtonTextWrapperClassFromSourceDomain(state);
}

function wrapButtonTextNodes(button, wrapperClass) {
  const textNodes = Array.from(button.childNodes || []).filter(
    (node) =>
      node.nodeType === 3 &&
      String(node.textContent || '')
        .replace(/\s+/g, ' ')
        .trim()
  );
  if (textNodes.length === 0) return false;

  for (const textNode of textNodes) {
    const span = button.ownerDocument.createElement('span');
    span.className = wrapperClass;
    span.textContent = textNode.textContent;
    textNode.parentNode.replaceChild(span, textNode);
  }
  return true;
}

function adaptButtonMarkupToCurrentStyle(html, state) {
  const wrapperClass = findCurrentButtonTextWrapperClass(state);
  if (!wrapperClass || typeof DOMParser === 'undefined') return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html || ''}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return html;

  let changed = false;
  const buttons = root.querySelectorAll(
    'a.cs-button-solid, button.cs-button-solid, a.cs-button-transparent, button.cs-button-transparent'
  );
  for (const button of buttons) {
    if (Array.from(button.children || []).some((child) => child.classList?.contains(wrapperClass)))
      continue;

    const meaningfulChildren = Array.from(button.childNodes || []).filter((node) => {
      if (node.nodeType === 3)
        return String(node.textContent || '')
          .replace(/\s+/g, ' ')
          .trim();
      return true;
    });
    const canWrapEverything =
      meaningfulChildren.length > 0 && meaningfulChildren.every((node) => node.nodeType === 3);

    if (canWrapEverything) {
      const span = doc.createElement('span');
      span.className = wrapperClass;
      while (button.firstChild) span.appendChild(button.firstChild);
      button.appendChild(span);
      changed = true;
      continue;
    }

    changed = wrapButtonTextNodes(button, wrapperClass) || changed;
  }

  return changed ? root.innerHTML : html;
}

export function matchCurrentButtonMarkup(section, state = store.getState()) {
  if (!section || typeof section !== 'object') return section;

  if (typeof section.html === 'string' && section.html.trim()) {
    section.html = adaptButtonMarkupToCurrentStyle(section.html, state);
  }

  if (typeof section.originalHtml === 'string' && section.originalHtml.trim()) {
    section.originalHtml = adaptButtonMarkupToCurrentStyle(section.originalHtml, state);
  }

  return section;
}

async function getSectionIndex() {
  if (sectionIndex) return sectionIndex;
  try {
    sectionIndex = await loadJSON('data/section-index.json');
    return sectionIndex;
  } catch (err) {
    console.error('[SectionManager] Failed to load section index:', err);
    return [];
  }
}

function createInstance(data, options = {}) {
  let sourceHTML = options.matchCurrentButtonMarkup
    ? adaptButtonMarkupToCurrentStyle(data.html, store.getState())
    : data.html;
  let sourceCSS = data.css;

  // Skin from URL projects carry a composition context: every insert path
  // (add, swap, template page) flows through here, so newly inserted catalog
  // sections arrive client-branded instead of reintroducing the source
  // business. Works on a copy — `data` is the shared catalog cache.
  const compositionContext = store.getState()?.compositionContext;
  if (compositionContext) {
    const working = {
      html: sourceHTML,
      css: sourceCSS,
      category: data.category,
      catalogKey: data.catalogKey,
      templateSiteName: data.templateSiteName
    };
    try {
      applyCompositionSkin(working, compositionContext);
      sourceHTML = working.html;
      sourceCSS = working.css;
    } catch (err) {
      console.warn('[SectionManager] composition skin failed — inserting unskinned:', err);
    }
  }

  const { html: stampedHtml } = stampEditableIds(sourceHTML);
  const instance = {
    instanceId: generateInstanceId(),
    catalogKey: data.catalogKey,
    sectionId: data.sectionId,
    category: data.category,
    type: 'template',
    html: stampedHtml,
    originalHtml: stampedHtml,
    css: sourceCSS,
    js: data.js || '',
    overrides: null
  };
  if (data.cssDedupKey) instance.cssDedupKey = data.cssDedupKey;
  if (data.templateSiteName) instance.templateSiteName = data.templateSiteName;
  if (data.templateSectionId) instance.templateSectionId = data.templateSectionId;
  if (typeof data.provenancePath === 'string' && data.provenancePath)
    instance.provenancePath = data.provenancePath;
  if (typeof data.sourceHash === 'string') instance.sourceHash = data.sourceHash;
  // Id ownership record from the PRISTINE catalog HTML (before skin/stamping):
  // the export id plan classifies any id absent from this set as user-authored
  // and never renames it. originalHtml cannot serve — it is a reset baseline.
  const idProvenance = computeIdProvenance(data.html);
  if (idProvenance) instance.idProvenance = idProvenance;
  return instance;
}

function isSplitFooterSectionVariant(data) {
  if (!data || typeof data !== 'object') return false;
  const desc = typeof data.description === 'string' ? data.description : '';
  return /\(\s*section\s*\)/i.test(desc);
}

function isSplitFooterFooterVariant(data) {
  if (!data || typeof data !== 'object') return false;
  const desc = typeof data.description === 'string' ? data.description : '';
  return /\(\s*footer\s*\)/i.test(desc);
}

function getSplitFooterStitchNumber(data) {
  if (!data || typeof data !== 'object') return null;
  if (Number.isFinite(data.stitchNumber)) return Number(data.stitchNumber);
  const key = typeof data.catalogKey === 'string' ? data.catalogKey : '';
  const match = key.match(/^stitch-(\d+)-\d+$/);
  return match ? Number.parseInt(match[1], 10) : null;
}

async function resolveSplitFooterCompanionData(footerData) {
  if (!isSplitFooterFooterVariant(footerData)) return null;
  const stitchNumber = getSplitFooterStitchNumber(footerData);
  if (!Number.isFinite(stitchNumber)) return null;

  const index = await getSectionIndex();
  const companionMeta = index.find(
    (entry) =>
      entry.category === 'footer' &&
      Number(entry.stitchNumber) === stitchNumber &&
      isSplitFooterSectionVariant(entry)
  );
  if (!companionMeta) return null;
  return await loadSectionData(companionMeta.catalogKey, companionMeta.category);
}

async function syncSplitFooterCompanionSection(footerData, pageId) {
  const companionData = await resolveSplitFooterCompanionData(footerData);
  if (!companionData) return { insertedInstanceId: null, replacedInstanceId: null, pageId: null };

  const ctx = getPagePath(pageId);
  if (!ctx) return { insertedInstanceId: null, replacedInstanceId: null, pageId: null };

  const currentSections = Array.isArray(ctx.page.sections) ? ctx.page.sections : [];
  if (currentSections.some((section) => section.catalogKey === companionData.catalogKey)) {
    return {
      insertedInstanceId: null,
      replacedInstanceId: null,
      pageId: pageId || store.getState().activePage
    };
  }

  const index = await getSectionIndex();
  const byCatalogKey = new Map(index.map((entry) => [entry.catalogKey, entry]));
  const existingSplitIdx = currentSections.findIndex((section) => {
    if (!section || section.category !== 'footer') return false;
    return isSplitFooterSectionVariant(byCatalogKey.get(section.catalogKey));
  });

  const nextSections = currentSections.slice();
  if (existingSplitIdx >= 0) {
    const existing = nextSections[existingSplitIdx];
    const replacement = createInstance(companionData, { matchCurrentButtonMarkup: true });
    replacement.instanceId = existing.instanceId;
    replacement.displayName = existing.displayName;
    nextSections[existingSplitIdx] = replacement;
    store.update(ctx.sectionsPath, nextSections, 'Sync split footer companion section');
    return {
      insertedInstanceId: null,
      replacedInstanceId: replacement.instanceId,
      pageId: pageId || store.getState().activePage
    };
  }

  const companionInstance = createInstance(companionData, { matchCurrentButtonMarkup: true });
  nextSections.push(companionInstance);
  store.update(ctx.sectionsPath, nextSections, 'Add split footer companion section');
  return {
    insertedInstanceId: companionInstance.instanceId,
    replacedInstanceId: null,
    pageId: pageId || store.getState().activePage
  };
}

/**
 * Template-specific catalog entries are generated from imported websites and
 * usually have `templateSiteName` metadata plus a `template-...` catalogKey.
 * These can be hidden in variant browsers to reduce noise.
 * @param {object} variant
 * @returns {boolean}
 */
export function isTemplateSpecificSectionVariant(variant) {
  if (!variant || typeof variant !== 'object') return false;
  if (typeof variant.templateSiteName === 'string' && variant.templateSiteName.trim()) return true;
  const key = typeof variant.catalogKey === 'string' ? variant.catalogKey : '';
  return key.startsWith('template-');
}

async function setSharedSectionFromData(data, category) {
  if (store.refuseIfReadOnly(`Set ${category}`)) return null;
  const sharedKey = sharedSectionStorePath(category);
  const existing = store.getState().sharedSections[category];
  const verb = existing ? 'Replaced' : 'Set';

  const instance = createInstance(data, { matchCurrentButtonMarkup: true });
  let companionSync = null;
  store.beginTransaction(`${verb} ${category}`);
  try {
    store.update(sharedKey, instance, `${verb} ${category}`);
    if (category === 'footer') {
      companionSync = await syncSplitFooterCompanionSection(data);
    }
    store.commitTransaction();
  } catch (err) {
    store.abortTransaction();
    throw err;
  }

  if (companionSync?.insertedInstanceId) {
    queueHint('added', {
      instanceId: companionSync.insertedInstanceId,
      pageId: companionSync.pageId
    });
  } else if (companionSync?.replacedInstanceId) {
    queueHint('swapped', {
      instanceId: companionSync.replacedInstanceId,
      pageId: companionSync.pageId
    });
  }

  await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });
  // PP-1: a shared-slot add is a mutation like any other and ends with a way
  // back. The toast is created AFTER the render, so the history-changed sweep
  // that store.update fired has already run and cannot dismiss it.
  if (showUndoToastFn) showUndoToastFn(`${verb} ${category}`, { id: 'undo-add-shared' });
  else if (showToastFn) showToastFn(`${verb} ${category}`, 'success');
  return instance.instanceId;
}

/** Emit pre-delete event so section-list can animate the row out. One-shot callback. */
async function _emitPreDelete(instanceId) {
  await new Promise((resolve) => {
    let resolved = false;
    const done = () => {
      if (resolved) return;
      resolved = true;
      clearTimeout(fallback);
      resolve();
    };
    eventBus.emit('feedback:pre-delete', { instanceId, done });
    const fallback = setTimeout(done, 400);
  });
}

/**
 * Add a section from the catalog at a given position (defaults to end).
 * @param {string} catalogKey
 * @param {string} category
 * @param {number} [position] - Insert index (defaults to end)
 * @param {string} [pageId] - Target page ID (defaults to active page)
 */
export async function addSection(catalogKey, category, position, pageId) {
  emitAsyncStatus(true, 'add', { category });
  try {
    const data = await loadSectionData(catalogKey, category);
    if (!data) return null;

    // Shared slots are singletons (navigation/footer), but split-footer sets
    // include companion "(section)" entries that should be inserted into page
    // content instead of replacing shared footer.
    const shouldRouteToShared =
      isSharedCategory(category) && (category !== 'footer' || !isSplitFooterSectionVariant(data));
    if (shouldRouteToShared) {
      // BC-5 add parity. A shared slot that already holds a section is
      // REPLACED through the path Replace uses — the keep-edits question, the
      // content merge, the mobile-menu carry-over, one history entry — instead
      // of being overwritten with the new variant's defaults. Adding a nav over
      // a customised nav used to lose every edit without asking.
      if (store.getState().sharedSections?.[category]) {
        const swapped = await swapSharedSection(category, catalogKey, category);
        return swapped?.ok ? store.getState().sharedSections?.[category]?.instanceId || null : null;
      }
      return await setSharedSectionFromData(data, category);
    }

    const ctx = getPagePath(pageId);
    if (!ctx) return null;

    const instance = createInstance(data, { matchCurrentButtonMarkup: true });
    const insertIndex = typeof position === 'number' ? position : ctx.page.sections.length;
    const activePageId = pageId || store.getState().activePage;
    if (!store.insertAt(ctx.sectionsPath, insertIndex, instance, `Add ${category} section`)) {
      return null;
    }
    queueHint('added', { instanceId: instance.instanceId, pageId: activePageId });

    await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });
    // PP-1: "Added Split hero · Undo" — the label is the section's own human
    // name (the catalog `description` the picker showed; createInstance does
    // not copy it onto the instance), falling back to the shared ladder. The
    // undo IS the success confirmation — never two toasts per add.
    const label =
      (typeof data.description === 'string' && data.description.trim()) ||
      getSectionDisplayLabel(instance);
    if (showUndoToastFn) showUndoToastFn(`Added ${label}`, { id: 'undo-add-section' });
    else if (showToastFn) showToastFn(`Added ${label}`, 'success');
    return instance.instanceId;
  } finally {
    emitAsyncStatus(false, 'add', { category });
  }
}

/**
 * Add or replace a shared section (navigation or footer).
 */
async function _addSharedSection(catalogKey, category) {
  emitAsyncStatus(true, 'add', { category });
  try {
    const data = await loadSectionData(catalogKey, category);
    if (!data) return null;
    return await setSharedSectionFromData(data, category);
  } finally {
    emitAsyncStatus(false, 'add', { category });
  }
}

/**
 * Add a blank freeform section at a given position.
 * @param {number} [position] - Insert index (defaults to end)
 * @param {string} [pageId] - Target page ID (defaults to active page)
 */
export async function addFreeformSection(position, pageId) {
  const ctx = getPagePath(pageId);
  if (!ctx) return null;

  const instance = createFreeformSection();
  const insertIndex = typeof position === 'number' ? position : ctx.page.sections.length;
  const activePageId = pageId || store.getState().activePage;
  if (!store.insertAt(ctx.sectionsPath, insertIndex, instance, 'Add blank section')) return null;
  queueHint('added', { instanceId: instance.instanceId, pageId: activePageId });

  await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });
  if (showUndoToastFn) showUndoToastFn('Added blank section', { id: 'undo-add-section' });
  else if (showToastFn) showToastFn('Added blank section', 'success');
  return instance.instanceId;
}

/**
 * Remove a section by instanceId.
 * @param {string} instanceId
 * @param {string} [pageId] - Target page ID (defaults to active page)
 * @param {object} [options]
 * @param {boolean} [options.skipConfirm=false] - Skip user confirmation dialog
 * @param {string} [options.confirmSectionLabel] - Optional section name shown in confirm modal
 * @param {string} [options.confirmThumbnailSrc] - Optional section thumbnail shown in confirm modal
 */
export async function removeSection(
  instanceId,
  pageId,
  { skipConfirm = false, confirmSectionLabel = '', confirmThumbnailSrc = '' } = {}
) {
  if (store.refuseIfReadOnly('Delete section')) return false;
  const ctx = getPagePath(pageId);
  if (!ctx) return false;
  const targetPageId = ctx.page.id;

  const idx = findSectionIndex(ctx.page, instanceId);
  if (idx === -1) return false;
  // Collab Phase 9: a peer editing this section blocks its removal.
  if (isSectionLockedByPeer(lockKeyForInstance(instanceId))) {
    notifyLockedDenied(lockKeyForInstance(instanceId));
    return false;
  }
  const section = ctx.page.sections[idx];
  const sectionLabel = (confirmSectionLabel || getSectionDisplayLabel(section) || 'Section').trim();

  if (!skipConfirm) {
    if (confirmFn) {
      const sectionThumb = confirmThumbnailSrc || (await getSectionConfirmThumbnailSrc(section));
      const isMac = typeof navigator !== 'undefined' && navigator.platform?.includes('Mac');
      const confirmed = await confirmFn({
        title: 'Delete Section',
        message: `"${sectionLabel}" and its content will be removed.`,
        details: `You can undo with ${isMac ? '⌘' : 'Ctrl+'}Z.`,
        confirmLabel: 'Delete',
        danger: true,
        contextLabel: sectionLabel,
        contextThumbnailSrc: sectionThumb,
        contextThumbnailAlt: `Preview of ${sectionLabel}`
      });
      if (!confirmed) return false;
    } else {
      return false;
    }
  }

  // Pre-delete slide-out animation (consumed by section-list)
  await _emitPreDelete(instanceId);

  // BC-1: the confirm dialog and the slide-out both await; resolve the index
  // again so a reorder in between cannot delete the neighbour. A content edit
  // does not matter here — the section is going either way.
  const liveCtx = getPagePath(targetPageId);
  const liveIdx = liveCtx ? findSectionIndex(liveCtx.page, instanceId) : -1;
  if (liveIdx === -1) return false;
  if (!store.removeAt(liveCtx.sectionsPath, liveIdx, 'Delete section')) return false;
  if (showUndoToastFn) showUndoToastFn('Section removed');
  await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });
  return true;
}

/**
 * Move a section to a new position index.
 * @param {string} instanceId
 * @param {number} newIndex
 * @param {string} [pageId] - Target page ID (defaults to active page)
 */
export async function moveSection(instanceId, newIndex, pageId) {
  if (store.refuseIfReadOnly('Move section')) return;
  const ctx = getPagePath(pageId);
  if (!ctx) return;

  const fromIndex = findSectionIndex(ctx.page, instanceId);
  if (fromIndex === -1) return;

  // Adjust target index: if moving forward, account for the removal of the source
  let toIndex = newIndex;
  if (fromIndex < toIndex) toIndex--;
  if (fromIndex === toIndex) return;

  const activePageId = pageId || store.getState().activePage;
  if (!store.moveInArray(ctx.sectionsPath, fromIndex, toIndex, 'Move section')) return;
  queueHint('moved', { instanceId, pageId: activePageId });
  // Stable toast id: repeated drags replace the toast instead of stacking.
  if (showUndoToastFn)
    showUndoToastFn(`Section moved to position ${toIndex + 1}`, { id: 'undo-move-section' });
  else if (showToastFn) showToastFn(`Section moved to position ${toIndex + 1}`, 'success');
  await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });
}

/**
 * Duplicate a section, creating a new instance with a fresh instanceId.
 * CSS is shared via [data-pb-section]; JS is independently scoped via [data-pb-instance].
 * @param {string} instanceId
 * @param {string} [pageId] - Target page ID (defaults to active page)
 */
export async function duplicateSection(instanceId, pageId) {
  if (store.refuseIfReadOnly('Duplicate section')) return null;
  // Backstop for keyboard/palette paths (mirrors copySectionById's shared guard):
  // shared sections live outside page arrays, so this would otherwise silently no-op.
  if (getSharedType(instanceId)) {
    if (showToastFn) showToastFn("Shared sections can't be duplicated", 'info');
    return null;
  }

  const ctx = getPagePath(pageId);
  if (!ctx) return null;

  const idx = findSectionIndex(ctx.page, instanceId);
  if (idx === -1) return null;

  const original = ctx.page.sections[idx];
  const clone = structuredClone(original);
  clone.instanceId = generateInstanceId();
  delete clone.editableIdRepairs;
  matchCurrentButtonMarkup(clone);

  const activePageId = pageId || store.getState().activePage;
  if (!store.insertAt(ctx.sectionsPath, idx + 1, clone, 'Duplicate section')) return null;
  queueHint('added', { instanceId: clone.instanceId, pageId: activePageId });
  await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });
  // PP-2: the clone is selected, scrolled to and flashed — the same "it
  // landed here" contract as an add. The shell answers this event.
  eventBus.emit('section:landed', { instanceId: clone.instanceId });
  if (showUndoToastFn) showUndoToastFn('Section duplicated', { id: 'undo-duplicate-section' });
  else if (showToastFn) showToastFn('Section duplicated', 'success');
  return clone.instanceId;
}

// ── Bulk structural operations (Polish 16A2b) ──────────────────────────────
// One confirm, one pre-delete race, one compound history entry, one render,
// one undo toast — the whole point of the phase. The choke-point order is
// load-bearing: names for the confirm are resolved FIRST and no indices are
// captured across the awaits; after the confirm and the animation (both of
// which a collab peer can span), everything — ids, indices, locks, revision —
// is RE-resolved before the pure patch build, and applyCompound's
// expectedRevision guard makes that revalidation meaningful.

/** Emit all pre-delete animations, raced against ONE shared 400ms budget. */
async function _emitBulkPreDelete(instanceIds) {
  const waits = instanceIds.map(
    (instanceId) =>
      new Promise((resolve) => {
        eventBus.emit('feedback:pre-delete', { instanceId, done: resolve });
      })
  );
  await Promise.race([Promise.all(waits), new Promise((resolve) => setTimeout(resolve, 400))]);
}

/**
 * Resolve a bulk target set against live state. Shared nav/footer are
 * partitioned out defensively (the selection store's invariant already keeps
 * them out of any multi-selection). Returns null when the page is gone or
 * every id vanished.
 */
function _resolveBulkTargets(instanceIds, pageId) {
  const ctx = getPagePath(pageId);
  if (!ctx) return null;
  const pageIdsOnly = [];
  const sharedSkipped = [];
  for (const id of instanceIds || []) {
    (getSharedType(id) ? sharedSkipped : pageIdsOnly).push(id);
  }
  const resolved = resolveAscending(ctx.page.sections, pageIdsOnly);
  return { ctx, resolved, sharedSkipped, pageIdsOnly };
}

/** First peer-locked id in the set, or null. */
function _firstLockedId(instanceIds) {
  for (const id of instanceIds) {
    if (isSectionLockedByPeer(lockKeyForInstance(id))) return id;
  }
  return null;
}

/** The failed-revalidation escape hatch: rows must not stay animated out. */
function _restoreBulkUI(instanceIds, { toast = true } = {}) {
  eventBus.emit('feedback:pre-delete-restore', { instanceIds });
  if (toast && showToastFn)
    showToastFn('Sections changed while confirming — nothing was deleted', 'info');
}

/**
 * Delete N sections as ONE undoable operation.
 * @param {string[]} instanceIds
 * @param {object} [options]
 * @param {string}  [options.pageId]
 * @param {boolean} [options.skipConfirm=false]
 * @param {string[]} [options.hiddenNames] - display names of selected sections
 *   currently hidden by the sidebar filter, named in the confirm before they
 *   are destroyed.
 * @returns {Promise<boolean>} true when the compound committed.
 */
export async function bulkDeleteSections(
  instanceIds,
  { pageId, skipConfirm = false, hiddenNames = [] } = {}
) {
  const initial = _resolveBulkTargets(instanceIds, pageId);
  if (!initial || !initial.resolved || initial.resolved.length === 0) return false;
  const lockedUpFront = _firstLockedId(initial.pageIdsOnly);
  if (lockedUpFront) {
    notifyLockedDenied(lockKeyForInstance(lockedUpFront));
    return false;
  }

  // 1: display names only — nothing index-shaped survives past the awaits.
  const count = initial.resolved.length;
  const names = initial.resolved.map(({ index }) =>
    getSectionDisplayLabel(initial.ctx.page.sections[index])
  );

  // 2: ONE confirm for the whole set.
  if (!skipConfirm) {
    if (!confirmFn) return false;
    const shown = names.slice(0, 8).map((n) => `"${n}"`);
    if (names.length > 8) shown.push(`and ${names.length - 8} more`);
    const isMac = typeof navigator !== 'undefined' && navigator.platform?.includes('Mac');
    const hiddenNote = hiddenNames.length
      ? ` Includes ${hiddenNames.length} hidden by the filter: ${hiddenNames
          .map((n) => `"${n}"`)
          .join(', ')}.`
      : '';
    const confirmed = await confirmFn({
      title: 'Delete Sections',
      message: `${count} sections and their content will be removed.`,
      details: `${shown.join(', ')}.${hiddenNote} You can undo with ${isMac ? '⌘' : 'Ctrl+'}Z.`,
      confirmLabel: `Delete ${count} sections`,
      danger: true
    });
    if (!confirmed) return false;
  }

  // 3: one shared animation budget, not N serial 400ms waits.
  await _emitBulkPreDelete(initial.pageIdsOnly);

  // 4: RE-resolve everything — a peer can have acted across either await.
  const fresh = _resolveBulkTargets(instanceIds, pageId);
  if (!fresh || !fresh.resolved || fresh.resolved.length === 0) {
    _restoreBulkUI(instanceIds);
    return false;
  }
  const lockedNow = _firstLockedId(fresh.pageIdsOnly);
  if (lockedNow) {
    notifyLockedDenied(lockKeyForInstance(lockedNow));
    _restoreBulkUI(instanceIds, { toast: false }); // the lock toast already explains
    return false;
  }

  // 5-7: pure build → atomic commit (validates again, against the SAME
  // revision captured after the last await — the store refuses otherwise).
  const expectedRevision = store.getRevision();
  const patches = buildBulkDeletePatches(
    fresh.ctx.page.sections,
    fresh.ctx.sectionsPath,
    fresh.pageIdsOnly
  );
  const committed =
    !!patches &&
    store.applyCompound({
      forward: patches.forward,
      reverse: patches.reverse,
      label: `Delete ${fresh.resolved.length} sections`,
      expectedRevision
    });
  if (!committed) {
    _restoreBulkUI(instanceIds);
    return false;
  }

  // 8-9: exactly one render, one summary undo toast.
  // BC-8: act-then-undo removed the dialog that listed rows the list filter
  // was hiding, so the toast carries that fact — deleting a row you cannot see
  // must still be said out loud.
  const hiddenNote = hiddenNames.length
    ? `, including ${hiddenNames.length} hidden by the filter`
    : '';
  if (showUndoToastFn)
    showUndoToastFn(`${fresh.resolved.length} sections removed${hiddenNote}`, {
      id: 'undo-bulk-delete'
    });
  await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });
  return true;
}

/**
 * Duplicate N sections as ONE undoable operation. Each clone lands
 * immediately after its original; selection moves to the clones via one
 * `selection:request` set. Shared sections are partitioned out and reported
 * (the one sanctioned partial: a category exclusion known up front,
 * non-destructive — mirrors duplicateSection's refusal).
 * @param {string[]} instanceIds
 * @returns {Promise<string[]|null>} the clone ids, or null.
 */
export async function bulkDuplicateSections(instanceIds, { pageId } = {}) {
  const targets = _resolveBulkTargets(instanceIds, pageId);
  if (!targets || !targets.resolved || targets.resolved.length === 0) return null;
  if (targets.sharedSkipped.length && showToastFn) {
    showToastFn("Shared sections can't be duplicated", 'info');
  }

  const expectedRevision = store.getRevision();
  const activePageId = pageId || store.getState().activePage;
  const patches = buildBulkDuplicatePatches(
    targets.ctx.page.sections,
    targets.ctx.sectionsPath,
    targets.pageIdsOnly,
    (section) => {
      // structuredClone keeps cssDedupKey — two instances of one catalog
      // section SHOULD share it, or every export duplicates the CSS block.
      const clone = structuredClone(section);
      clone.instanceId = generateInstanceId();
      delete clone.editableIdRepairs;
      matchCurrentButtonMarkup(clone);
      return clone;
    }
  );
  if (!patches) return null;

  // Hints queue BEFORE the notify/render boundary — a consumer that runs on
  // the commit's own notify would otherwise miss a hint queued after it.
  for (const cloneId of patches.cloneIds) {
    queueHint('added', { instanceId: cloneId, pageId: activePageId });
  }
  const committed = store.applyCompound({
    forward: patches.forward,
    reverse: patches.reverse,
    label: `Duplicate ${patches.cloneIds.length} sections`,
    expectedRevision
  });
  if (!committed) return null;

  // ONE set — the controller applies policy; never N select events.
  eventBus.emit('selection:request', {
    intent: 'set',
    ids: patches.cloneIds,
    source: 'bulk-duplicate'
  });
  if (showUndoToastFn)
    showUndoToastFn(`${patches.cloneIds.length} sections duplicated`, {
      id: 'undo-bulk-duplicate'
    });
  await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });
  return patches.cloneIds;
}

/**
 * Reorder N sections as ONE undoable operation — collapse-and-shift for
 * keyboard nudges, drop-at-gap for drag. The proposed permutation is compared
 * to the current order and an IDENTICAL permutation commits nothing: no
 * history entry, no render, no toast.
 * @param {string[]} instanceIds
 * @param {object} options - exactly one of:
 * @param {'up'|'down'} [options.direction] - keyboard nudge
 * @param {number} [options.targetIndex]    - drag gap index (pre-shift, the
 *   same convention the drop indicator reports)
 * @returns {Promise<boolean>} true when a reorder committed.
 */
export async function bulkMoveSections(instanceIds, { pageId, direction, targetIndex } = {}) {
  const targets = _resolveBulkTargets(instanceIds, pageId);
  if (!targets || !targets.resolved || targets.resolved.length === 0) return false;
  const locked = _firstLockedId(targets.pageIdsOnly);
  if (locked) {
    notifyLockedDenied(lockKeyForInstance(locked));
    return false;
  }

  const currentOrder = targets.ctx.page.sections.map((s) => s.instanceId);
  const selectedSet = new Set(targets.pageIdsOnly);
  const targetOrder =
    direction === 'up' || direction === 'down'
      ? targetOrderForNudge(currentOrder, selectedSet, direction)
      : targetOrderForGap(currentOrder, selectedSet, targetIndex);
  const expectedRevision = store.getRevision();
  const patches = buildMovePatchesForOrder(currentOrder, targetOrder, targets.ctx.sectionsPath);
  if (!patches) return false; // identical permutation — zero entries, by design

  const activePageId = pageId || store.getState().activePage;
  for (const id of targets.pageIdsOnly) {
    queueHint('moved', { instanceId: id, pageId: activePageId });
  }
  const committed = store.applyCompound({
    forward: patches.forward,
    reverse: patches.reverse,
    label: `Reorder ${targets.resolved.length} sections`,
    expectedRevision
  });
  if (!committed) return false;

  if (showUndoToastFn)
    showUndoToastFn(`${targets.resolved.length} sections moved`, { id: 'undo-bulk-move' });
  await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });
  return true;
}

/** Replacement keeps old lineage inactive and reserves every recorded identity. */
async function repairStampOptions(section, { freshBaseline = false } = {}) {
  if (!Object.hasOwn(section, 'editableIdRepairs')) return {};
  const revision = store.getRevision();
  const verified = await verifyEditableIdRepairs(section.editableIdRepairs, {
    instanceId: section.instanceId
  });
  if (!verified.ok || store.getRevision() !== revision) {
    showToastFn?.(
      'Element identity history cannot be verified. The section was kept unchanged.',
      'error'
    );
    return null;
  }
  return { editableIdRepairs: verified.value, freshBaseline };
}

/**
 * Reset a section (and all child edits) back to its original snapshot.
 * Clears section overrides and restores original stamped HTML.
 * @param {string} instanceId
 * @param {string} [pageId] - Target page ID (defaults to active page)
 * @param {object} [options]
 * @param {boolean} [options.skipConfirm=false] - Skip user confirmation dialog
 * @param {string} [options.confirmSectionLabel] - Optional section name shown in confirm modal
 * @param {string} [options.confirmThumbnailSrc] - Optional section thumbnail shown in confirm modal
 */
export async function resetSection(
  instanceId,
  pageId,
  { skipConfirm = false, confirmSectionLabel = '', confirmThumbnailSrc = '' } = {}
) {
  if (store.refuseIfReadOnly('Reset section')) return false;
  const ctx = getPagePath(pageId);
  if (!ctx) return false;
  const targetPageId = ctx.page.id;

  const idx = findSectionIndex(ctx.page, instanceId);
  if (idx === -1) return false;

  const current = ctx.page.sections[idx];
  if (!current) return false;
  // Collab Phase 9: don't reset a section a peer is editing.
  if (isSectionLockedByPeer(lockKeyForInstance(instanceId))) {
    notifyLockedDenied(lockKeyForInstance(instanceId));
    return false;
  }
  const repairOptions = await repairStampOptions(current);
  if (!repairOptions) return false;
  const sectionLabel = (confirmSectionLabel || getSectionDisplayLabel(current) || 'Section').trim();

  if (!skipConfirm) {
    if (confirmFn) {
      const sectionThumb = confirmThumbnailSrc || (await getSectionConfirmThumbnailSrc(current));
      const isMac = typeof navigator !== 'undefined' && navigator.platform?.includes('Mac');
      const confirmed = await confirmFn({
        title: 'Reset Section',
        message: `"${sectionLabel}" and all nested element edits will be reset to the original template state.`,
        details: `You can undo with ${isMac ? '⌘' : 'Ctrl+'}Z.`,
        confirmLabel: 'Reset',
        danger: true,
        contextLabel: sectionLabel,
        contextThumbnailSrc: sectionThumb,
        contextThumbnailAlt: `Preview of ${sectionLabel}`
      });
      if (!confirmed) return false;
    } else {
      return false;
    }
  }

  let resetHtml = '';
  let resetOriginalHtml = '';

  if (typeof current.originalHtml === 'string' && current.originalHtml.trim()) {
    const { html: stampedOriginal } = stampEditableIds(current.originalHtml, {
      ...repairOptions,
      freshBaseline: Boolean(
        repairOptions.editableIdRepairs &&
        (await sha256Hex(canonicalizeAssetBackedMediaHTML(current.originalHtml))) !==
          repairOptions.editableIdRepairs.records.at(-1).afterOriginalHtmlHash
      )
    });
    resetHtml = stampedOriginal;
    resetOriginalHtml = stampedOriginal;
  } else if (current.catalogKey && current.category) {
    const originalData = await loadSectionData(current.catalogKey, current.category);
    if (originalData?.html) {
      const { html: stampedOriginal } = stampEditableIds(originalData.html, {
        ...repairOptions,
        freshBaseline: true
      });
      resetHtml = stampedOriginal;
      resetOriginalHtml = stampedOriginal;
    }
  }

  // Final fallback for legacy/unknown sections: normalize current HTML.
  if (!resetHtml) {
    const { html: stampedCurrent } = stampEditableIds(current.html || '', {
      ...repairOptions,
      freshBaseline: true
    });
    resetHtml = stampedCurrent;
    resetOriginalHtml = stampedCurrent;
  }

  const next = {
    ...current,
    html: resetHtml,
    originalHtml: resetOriginalHtml,
    overrides: null
  };

  const live = reresolvePageSection(targetPageId, instanceId, current);
  if (!live.ok) {
    announceStaleTarget(live.reason, 'reset');
    return false;
  }
  if (isSectionLockedByPeer(lockKeyForInstance(instanceId))) {
    notifyLockedDenied(lockKeyForInstance(instanceId));
    return false;
  }
  if (!store.update(`${live.ctx.sectionsPath}.${live.idx}`, next, 'Reset section')) return false;
  // Prevent visible white iframe nav flash during reset by keeping iframe
  // hidden until the new document is ready.
  await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: true });
  // PP-2 close-out: a reset replaces the section's content wholesale; it ends
  // with a way back like every other replace.
  if (showUndoToastFn) showUndoToastFn('Section reset', { id: 'undo-reset-section' });
  else if (showToastFn) showToastFn('Section reset', 'success');
  return true;
}

/**
 * Normalize extracted content for comparison: trim/collapse whitespace,
 * strip transient attributes from innerHTML.
 */
function _normalizeExtracted(items) {
  return items.map((item) => {
    const key = `${item.selector}[${item.index}]`;
    if (item.type === 'text') {
      const normalized = item.content
        .replace(/\s*data-pb-eid="[^"]*"/g, '')
        .replace(/\s*data-pb-editable="[^"]*"/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      return `${key}:text:${normalized}`;
    }
    if (item.type === 'image') {
      return `${key}:img:${item.content.src}`;
    }
    if (item.type === 'link') {
      const text = (item.content.text || '').replace(/\s+/g, ' ').trim();
      return `${key}:link:${item.content.href}|${text}`;
    }
    return key;
  });
}

function _swapOverrideRisk(overrides) {
  if (!overrides || typeof overrides !== 'object') {
    return { elementTweaks: 0, customCSS: false };
  }
  let elementTweaks = 0;
  const countElements = (elements) => {
    for (const props of Object.values(elements || {})) {
      if (!props || typeof props !== 'object') continue;
      elementTweaks += Object.keys(props).filter((key) => key !== '__important').length;
    }
  };
  countElements(overrides.elements);
  for (const bucket of Object.values(overrides.responsive || {})) {
    countElements(bucket?.elements);
  }
  return {
    elementTweaks,
    customCSS: typeof overrides.customCSS === 'string' && overrides.customCSS.trim().length > 0
  };
}

/** Preserve section-level settings while dropping selectors/eids unsafe across variants. */
export function stripUnsafeSwapOverrides(overrides) {
  if (!overrides || typeof overrides !== 'object') return null;
  const next = structuredClone(overrides);
  delete next.elements;
  delete next.customCSS;
  if (next.__important && typeof next.__important === 'object') {
    const flags = { ...next.__important };
    delete flags.customCSS;
    if (Object.keys(flags).length) next.__important = flags;
    else delete next.__important;
  }
  if (next.responsive && typeof next.responsive === 'object') {
    for (const viewport of Object.keys(next.responsive)) {
      const bucket = { ...(next.responsive[viewport] || {}) };
      delete bucket.elements;
      delete bucket.customCSS;
      if (Object.keys(bucket).length) next.responsive[viewport] = bucket;
      else delete next.responsive[viewport];
    }
    if (!Object.keys(next.responsive).length) delete next.responsive;
  }
  return Object.keys(next).length ? next : null;
}

/**
 * Check if a section has user edits by comparing its content against the original
 * catalog stitch, and whether the swap target has structural risk.
 * Returns 'keep-edits' | 'use-defaults' | 'cancel' | null (null = no prompt needed).
 */
async function _resolveSwapChoice(oldSection, newHtml, category) {
  if (!promptSwapChoiceFn) return 'keep-edits';

  // Detect edits by comparing normalized extracted content against original stitch
  let hasEdits = false;
  try {
    const originalData = await loadSectionData(oldSection.catalogKey, oldSection.category);
    if (originalData) {
      // For nav sections, also compare unmapped fields (phone, social, non-logo images)
      // using generic selectors so edits to those are detected even though they aren't mapped.
      const detectCategory = category === 'navigation' ? undefined : category;
      const originalExtracted = _normalizeExtracted(
        extractContent(originalData.html, detectCategory)
      );
      const currentExtracted = _normalizeExtracted(extractContent(oldSection.html, detectCategory));
      if (originalExtracted.length !== currentExtracted.length) {
        hasEdits = true;
      } else {
        hasEdits = originalExtracted.some((v, i) => v !== currentExtracted[i]);
      }
    }
  } catch {
    // If we can't load original, assume no edits (conservative — avoids false prompts)
  }

  const overrideRisk = _swapOverrideRisk(oldSection.overrides);
  const hasUnsafeOverrides = overrideRisk.elementTweaks > 0 || overrideRisk.customCSS;

  // Check structural risk.
  const { risky, details } = assessSwapRisk(oldSection.html, newHtml, category);
  if ((!hasEdits || !risky) && !hasUnsafeOverrides) return 'keep-edits';

  // A remembered content-mapping choice must never silently authorize dropping
  // element tweaks or selector-based CSS on a later variant.
  const pref = _swapPreference.get(category);
  if (pref && !hasUnsafeOverrides) return pref;

  const warnings = [];
  if (risky && details) warnings.push(details);
  if (overrideRisk.elementTweaks > 0) {
    warnings.push(
      `${overrideRisk.elementTweaks} element tweak${overrideRisk.elementTweaks === 1 ? '' : 's'} cannot transfer safely and will be removed.`
    );
  }
  if (overrideRisk.customCSS) {
    warnings.push('Custom CSS targets the old variant and will be removed.');
  }

  // Prompt user
  const result = await promptSwapChoiceFn({
    category,
    // PP-2: the dialog states what it is acting on, under its title.
    sectionLabel: getSectionDisplayLabel(oldSection),
    details: warnings.join(' '),
    hasContentEdits: hasEdits,
    hasUnsafeOverrides
  });
  if (result.remember && result.choice !== 'cancel') {
    _swapPreference.set(category, result.choice);
  }
  return result.choice;
}

/**
 * Swap a section's variant: replace the section with a different catalog section
 * from the same category, preserving as much content as possible.
 * Uses store.update() on the array element path for a single atomic history entry.
 * @param {string} instanceId
 * @param {string} newCatalogKey
 * @param {string} newCategory
 * @param {string} [pageId] - Target page ID (defaults to active page)
 */
export async function swapSection(instanceId, newCatalogKey, newCategory, pageId) {
  // BC-1: an outcome, never a silent `undefined`. Callers (the variant picker)
  // decide whether to close on `ok` and whether a cancel returns the user to
  // where they were.
  if (store.refuseIfReadOnly('Swap variant')) return { ok: false, reason: 'read-only' };
  const ctx = getPagePath(pageId);
  if (!ctx) return { ok: false, reason: 'gone' };
  const targetPageId = ctx.page.id;

  const idx = findSectionIndex(ctx.page, instanceId);
  if (idx === -1) return { ok: false, reason: 'gone' };
  // Collab Phase 9: don't swap a variant of a section a peer is editing.
  if (isSectionLockedByPeer(lockKeyForInstance(instanceId))) {
    notifyLockedDenied(lockKeyForInstance(instanceId));
    return { ok: false, reason: 'locked' };
  }

  const oldSection = ctx.page.sections[idx];
  const repairOptions = await repairStampOptions(oldSection, { freshBaseline: true });
  if (!repairOptions) return { ok: false, reason: 'repair' };
  emitAsyncStatus(true, 'swap', { category: newCategory, instanceId });
  try {
    const newData = await loadSectionData(newCatalogKey, newCategory);
    if (!newData) return { ok: false, reason: 'load' };

    const newHtml = adaptButtonMarkupToCurrentStyle(newData.html, store.getState());

    // Check if user has edits + structural risk → prompt if needed
    const swapChoice = await _resolveSwapChoice(oldSection, newHtml, oldSection.category);
    if (swapChoice === 'cancel') return { ok: false, cancelled: true };

    // Create new instance preserving the instanceId
    const newInstance = {
      instanceId: oldSection.instanceId,
      catalogKey: newData.catalogKey,
      sectionId: newData.sectionId,
      category: newData.category,
      html: newHtml,
      css: newData.css,
      js: newData.js || '',
      overrides: stripUnsafeSwapOverrides(oldSection.overrides),
      displayName: oldSection.displayName
    };
    if (Object.hasOwn(oldSection, 'editableIdRepairs'))
      newInstance.editableIdRepairs = structuredClone(oldSection.editableIdRepairs);
    // Provenance follows the file actually loaded, never the old instance's.
    if (typeof newData.provenancePath === 'string' && newData.provenancePath)
      newInstance.provenancePath = newData.provenancePath;
    if (typeof newData.sourceHash === 'string') newInstance.sourceHash = newData.sourceHash;
    const swapIdProvenance = computeIdProvenance(newData.html);
    if (swapIdProvenance) newInstance.idProvenance = swapIdProvenance;

    // Stamp the fresh variant HTML as the new originalHtml baseline
    const { html: freshStamped } = stampEditableIds(newInstance.html, repairOptions);
    newInstance.originalHtml = freshStamped;

    let mismatches = 0;
    if (swapChoice === 'keep-edits') {
      const extracted = extractContent(oldSection.html, oldSection.category);
      const { html: mergedHtml, mismatches: m } = applyContent(
        newHtml,
        extracted,
        newData.category
      );
      mismatches = m;
      const matchedHtml = adaptButtonMarkupToCurrentStyle(mergedHtml, store.getState());
      const { html: stampedHtml } = stampEditableIds(matchedHtml, repairOptions);
      newInstance.html = stampedHtml;
    } else {
      // 'use-defaults' — fresh variant HTML, clear overrides
      newInstance.html = freshStamped;
      newInstance.overrides = null;
    }

    // Atomic replace: single history entry via store.update on the array element
    // path — the path RE-RESOLVED now, not the index read before the awaits.
    const live = reresolvePageSection(targetPageId, instanceId, oldSection);
    if (!live.ok) {
      announceStaleTarget(live.reason, 'replacement');
      return { ok: false, reason: live.reason };
    }
    if (isSectionLockedByPeer(lockKeyForInstance(instanceId))) {
      notifyLockedDenied(lockKeyForInstance(instanceId));
      return { ok: false, reason: 'locked' };
    }
    if (
      !store.update(
        `${live.ctx.sectionsPath}.${live.idx}`,
        newInstance,
        `Swap to ${newCategory} variant`
      )
    ) {
      return { ok: false, reason: 'rejected' };
    }
    queueHint('swapped', { instanceId, pageId: targetPageId });

    await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });

    const swapLabel = replacedLabel(newInstance, mismatches);
    if (showUndoToastFn) showUndoToastFn(swapLabel);
    else if (showToastFn) showToastFn(swapLabel, mismatches > 0 ? 'info' : 'success');
    return { ok: true };
  } finally {
    emitAsyncStatus(false, 'swap', { category: newCategory, instanceId });
  }
}

/**
 * Swap a shared section's variant (navigation or footer).
 * @param {'navigation'|'footer'} sharedType
 * @param {string} newCatalogKey
 * @param {string} newCategory
 */
export async function swapSharedSection(sharedType, newCatalogKey, newCategory) {
  if (store.refuseIfReadOnly('Swap variant')) return { ok: false, reason: 'read-only' };
  if (newCategory !== sharedType) {
    console.warn(`[SectionManager] Cannot swap ${sharedType} with category "${newCategory}"`);
    return { ok: false, reason: 'category' };
  }

  const oldSection = store.getState().sharedSections[sharedType];
  if (!oldSection) return { ok: false, reason: 'gone' };
  if (isSectionLockedByPeer(lockKeyForShared(sharedType))) {
    notifyLockedDenied(lockKeyForShared(sharedType));
    return { ok: false, reason: 'locked' };
  }

  const repairOptions = await repairStampOptions(oldSection, { freshBaseline: true });
  if (!repairOptions) return { ok: false, reason: 'repair' };
  const sharedKey = `sharedSections.${sharedType}`;
  emitAsyncStatus(true, 'swap', { category: newCategory, instanceId: oldSection.instanceId });
  try {
    const newData = await loadSectionData(newCatalogKey, newCategory);
    if (!newData) return { ok: false, reason: 'load' };

    const newHtml = adaptButtonMarkupToCurrentStyle(newData.html, store.getState());

    // Check if user has edits + structural risk → prompt if needed
    const swapChoice = await _resolveSwapChoice(oldSection, newHtml, sharedType);
    if (swapChoice === 'cancel') return { ok: false, cancelled: true };

    const newInstance = {
      instanceId: oldSection.instanceId,
      catalogKey: newData.catalogKey,
      sectionId: newData.sectionId,
      category: newData.category,
      type: 'template',
      html: newHtml,
      css: newData.css,
      js: newData.js || '',
      overrides: stripUnsafeSwapOverrides(oldSection.overrides),
      displayName: oldSection.displayName
    };
    if (Object.hasOwn(oldSection, 'editableIdRepairs'))
      newInstance.editableIdRepairs = structuredClone(oldSection.editableIdRepairs);
    // Provenance follows the file actually loaded, never the old instance's.
    if (typeof newData.provenancePath === 'string' && newData.provenancePath)
      newInstance.provenancePath = newData.provenancePath;
    if (typeof newData.sourceHash === 'string') newInstance.sourceHash = newData.sourceHash;
    const swapIdProvenance = computeIdProvenance(newData.html);
    if (swapIdProvenance) newInstance.idProvenance = swapIdProvenance;

    // Stamp the fresh variant HTML as the new originalHtml baseline
    const { html: freshStamped } = stampEditableIds(newInstance.html, repairOptions);
    newInstance.originalHtml = freshStamped;

    let mismatches = 0;
    if (swapChoice === 'keep-edits') {
      const extracted = extractContent(oldSection.html, sharedType);
      const { html: mergedHtml, mismatches: m } = applyContent(
        newHtml,
        extracted,
        newData.category
      );
      mismatches = m;
      const matchedHtml = adaptButtonMarkupToCurrentStyle(mergedHtml, store.getState());
      const { html: stampedHtml } = stampEditableIds(matchedHtml, repairOptions);
      newInstance.html = stampedHtml;
    } else {
      newInstance.html = freshStamped;
      newInstance.overrides = null;
    }

    // Re-apply an active mobile-menu fragment to the new variant (MM-2). The
    // merge is pure and runs BEFORE the transaction; its result folds into
    // newInstance so the single update below stays the only state write —
    // never a nested transaction. On failure the fragment simply does not
    // carry over (the new variant keeps its own stock drawer).
    let mobileMenuDropped = false;
    if (sharedType === 'navigation' && oldSection.mobileMenu?.fragmentKey) {
      const fragment = await loadSectionData(oldSection.mobileMenu.fragmentKey, 'mobile-menu');
      const merge = fragment
        ? mergeMobileMenu(newInstance, fragment, createCssomCssEngine(), {
            theme: store.getState().theme
          })
        : { ok: false };
      if (merge.ok) {
        newInstance.html = merge.nextNav.html;
        newInstance.css = merge.nextNav.css;
        newInstance.mobileMenu = merge.nextNav.mobileMenu;
        newInstance.overrides = pruneOverridesForMerge(
          newInstance.overrides,
          newInstance.html,
          merge.report.survivingEids
        );
      } else {
        mobileMenuDropped = true;
      }
    }

    // BC-1: the shared slot is a singleton, so identity cannot move — but it can
    // be replaced or edited while the variant loaded and the prompt was open.
    const liveShared = store.getState().sharedSections[sharedType];
    if (
      !liveShared ||
      liveShared.instanceId !== oldSection.instanceId ||
      liveShared.html !== oldSection.html
    ) {
      announceStaleTarget(liveShared ? 'changed' : 'gone', 'replacement');
      return { ok: false, reason: liveShared ? 'changed' : 'gone' };
    }
    if (store.refuseIfReadOnly('Swap variant')) return { ok: false, reason: 'read-only' };

    let companionSync = null;
    store.beginTransaction(`Swap ${sharedType} variant`);
    try {
      store.update(sharedKey, newInstance, `Swap ${sharedType} variant`);
      if (sharedType === 'footer') {
        companionSync = await syncSplitFooterCompanionSection(newData);
      }
      store.commitTransaction();
    } catch (err) {
      store.abortTransaction();
      throw err;
    }

    if (companionSync?.insertedInstanceId) {
      queueHint('added', {
        instanceId: companionSync.insertedInstanceId,
        pageId: companionSync.pageId
      });
    } else if (companionSync?.replacedInstanceId) {
      queueHint('swapped', {
        instanceId: companionSync.replacedInstanceId,
        pageId: companionSync.pageId
      });
    }

    await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });

    const swapLabel = replacedLabel(newInstance, mismatches);
    if (showUndoToastFn) showUndoToastFn(swapLabel);
    else if (showToastFn) showToastFn(swapLabel, mismatches > 0 ? 'info' : 'success');
    if (mobileMenuDropped && showToastFn) {
      showToastFn(
        'The mobile menu could not be applied to this variant — it uses its stock menu',
        'info'
      );
    }
    return { ok: true };
  } finally {
    emitAsyncStatus(false, 'swap', { category: newCategory, instanceId: oldSection.instanceId });
  }
}

let _mobileMenuInFlight = false;

/**
 * Apply a mobile-menu fragment to the shared navigation (MM-2). One state
 * write — a single history entry — then a full render (nav scripts bind
 * listeners; surgical sync would orphan them).
 * @param {string} fragmentKey - mobile-menu catalog key (mm-*)
 * @param {Object} [opts]
 * @param {Object} [opts.cssEngine] - test seam; Node suites inject the
 *   css-tree engine because jsdom has no constructable CSSStyleSheet.
 */
export async function applyMobileMenuFragment(fragmentKey, opts = {}) {
  const nav = store.getState().sharedSections.navigation;
  if (!nav) {
    if (showToastFn) showToastFn('Add a navigation section first', 'info');
    return false;
  }
  if (_mobileMenuInFlight) return false;
  if (isSectionLockedByPeer(lockKeyForShared('navigation'))) {
    notifyLockedDenied(lockKeyForShared('navigation'));
    return false;
  }
  _mobileMenuInFlight = true;
  emitAsyncStatus(true, 'mobile-menu', { instanceId: nav.instanceId });
  try {
    if (!(await repairStampOptions(nav))) return false;
    const fragment = await loadSectionData(fragmentKey, 'mobile-menu');
    if (!fragment) return false;
    const result = mergeMobileMenu(nav, fragment, opts.cssEngine || createCssomCssEngine(), {
      theme: opts.theme || store.getState().theme
    });
    if (!result.ok) {
      const copy = (result.reasons || [])
        .map((reason) => ELIGIBILITY_REASON_COPY[reason])
        .filter(Boolean)
        .join(' ');
      if (showToastFn) {
        showToastFn(
          copy || 'This mobile menu cannot be applied to the current navigation',
          'error'
        );
      }
      return false;
    }
    const nextNav = {
      ...nav,
      html: result.nextNav.html,
      css: result.nextNav.css,
      mobileMenu: result.nextNav.mobileMenu,
      overrides: pruneOverridesForMerge(
        nav.overrides,
        result.nextNav.html,
        result.report.survivingEids
      )
    };
    store.update('sharedSections.navigation', nextNav, MOBILE_MENU_LABELS.apply);
    await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });
    if (showUndoToastFn) showUndoToastFn('Mobile menu applied');
    else if (showToastFn) showToastFn('Mobile menu applied', 'success');
    return true;
  } finally {
    _mobileMenuInFlight = false;
    emitAsyncStatus(false, 'mobile-menu', { instanceId: nav.instanceId });
  }
}

/**
 * Restore the navigation's stock drawer from the snapshot captured at first
 * fragment apply (MM-2, D5). Never re-fetches by catalogKey.
 */
export async function resetMobileMenuToStock(opts = {}) {
  const nav = store.getState().sharedSections.navigation;
  if (!nav?.mobileMenu) return false;
  if (_mobileMenuInFlight) return false;
  if (isSectionLockedByPeer(lockKeyForShared('navigation'))) {
    notifyLockedDenied(lockKeyForShared('navigation'));
    return false;
  }
  _mobileMenuInFlight = true;
  emitAsyncStatus(true, 'mobile-menu', { instanceId: nav.instanceId });
  try {
    if (!(await repairStampOptions(nav))) return false;
    const result = revertMobileMenu(nav, opts.cssEngine || createCssomCssEngine());
    if (!result.ok) {
      if (showToastFn) showToastFn('Could not restore the stock mobile menu', 'error');
      return false;
    }
    const nextNav = {
      ...nav,
      html: result.nextNav.html,
      css: result.nextNav.css,
      mobileMenu: null,
      overrides: pruneOverridesForMerge(
        nav.overrides,
        result.nextNav.html,
        result.report.survivingEids
      )
    };
    store.update('sharedSections.navigation', nextNav, MOBILE_MENU_LABELS.reset);
    await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });
    if (showUndoToastFn) showUndoToastFn('Mobile menu reset');
    else if (showToastFn) showToastFn('Mobile menu reset', 'success');
    return true;
  } finally {
    _mobileMenuInFlight = false;
    emitAsyncStatus(false, 'mobile-menu', { instanceId: nav.instanceId });
  }
}

/**
 * Remove a shared section (navigation or footer) with undo.
 * Confirmation is the default for existing list/menu callers. Surfaces whose
 * contract is act-then-undo (the command bar) pass `skipConfirm: true`, exactly
 * as they do for ordinary and bulk section deletion.
 * @param {'navigation'|'footer'} sharedType
 * @param {{skipConfirm?: boolean}} [opts]
 */
export async function removeSharedSection(sharedType, opts = {}) {
  if (store.refuseIfReadOnly('Delete section')) return false;
  const state = store.getState();
  const section = state.sharedSections[sharedType];
  if (!section) return false;
  // Collab Phase 9: don't remove a shared section a peer is editing.
  if (isSectionLockedByPeer(lockKeyForShared(sharedType))) {
    notifyLockedDenied(lockKeyForShared(sharedType));
    return false;
  }

  const label = sharedSectionLabel(sharedType) || 'Section';

  if (!opts.skipConfirm && confirmFn) {
    const isMac = typeof navigator !== 'undefined' && navigator.platform?.includes('Mac');
    const confirmed = await confirmFn({
      title: `Delete ${label}`,
      message: `The shared ${label.toLowerCase()} will be removed from all pages.`,
      details: `You can undo with ${isMac ? '⌘' : 'Ctrl+'}Z.`,
      confirmLabel: 'Delete',
      danger: true
    });
    if (!confirmed) return false;
  } else if (!opts.skipConfirm) {
    return false;
  }

  if (!store.update(`sharedSections.${sharedType}`, null, `Remove ${label.toLowerCase()}`)) {
    return false;
  }
  if (showUndoToastFn) showUndoToastFn(`${label} removed`);
  await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });
  return true;
}

/**
 * Check if an instanceId belongs to a shared section.
 * @param {string} instanceId
 * @returns {'navigation'|'footer'|null}
 */
export function getSharedType(instanceId) {
  if (!instanceId) return null;
  const { sharedSections } = store.getState();
  if (sharedSections.navigation?.instanceId === instanceId) return 'navigation';
  if (sharedSections.footer?.instanceId === instanceId) return 'footer';
  return null;
}

export async function loadPageMap(siteName) {
  try {
    return await loadJSON(`data/templates/${siteName}/_page-map.json`);
  } catch {
    return null;
  }
}

function createTemplatePage(
  name = 'Home',
  sourceTemplateDocument = null,
  sourceTemplateSectionPlacement = null
) {
  const page = {
    id: generatePageId(),
    name,
    sections: [],
    seo: { title: '', description: '', ogImage: '', canonicalUrl: '' }
  };
  const doc = normalizeSourceTemplateDocument(sourceTemplateDocument);
  if (doc) page.sourceTemplateDocument = doc;
  const placement = normalizeSourceTemplateSectionPlacement(sourceTemplateSectionPlacement);
  if (placement) page.sourceTemplateSectionPlacement = placement;
  return page;
}

async function loadAndCache(siteName, sectionId, role, lookups) {
  const key = `${siteName}::${sectionId}`;
  if (_sectionFetchCache.has(key)) return _sectionFetchCache.get(key);

  const promise = (async () => {
    let data = await loadTemplateSectionData(siteName, sectionId);
    if (!data) {
      const match = findBestCatalogMatch({ id: sectionId, role }, lookups);
      if (match) data = await loadSectionData(match.catalogKey, match.category);
    }
    return data;
  })();
  _sectionFetchCache.set(key, promise);
  try {
    const data = await promise;
    if (!data && _sectionFetchCache.get(key) === promise) _sectionFetchCache.delete(key);
    return data;
  } catch (error) {
    if (_sectionFetchCache.get(key) === promise) _sectionFetchCache.delete(key);
    throw error;
  }
}

export async function __loadAndCacheForTests(siteName, sectionId, role, lookups) {
  return loadAndCache(siteName, sectionId, role, lookups);
}

export function __resetSectionFetchCacheForTests() {
  _sectionFetchCache.clear();
}

/**
 * Run `mapper` over `items` with at most `limit` concurrent invocations.
 * Preserves input order in the result array.
 */
export async function runWithConcurrency(items, limit, mapper) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const results = new Array(items.length);
  let cursor = 0;
  const workerCount = Math.min(limit, items.length);
  const workers = Array.from({ length: workerCount }, async () => {
    while (cursor < items.length) {
      const i = cursor++;
      results[i] = await mapper(items[i], i);
    }
  });
  await Promise.all(workers);
  return results;
}

/**
 * Hydrate a page that was inserted with `_pendingTemplateRefs` (deferred
 * sections from a multi-page template load). Idempotent: if the page is
 * already hydrated, deleted, or another call is in flight, returns the
 * existing promise / a no-op.
 *
 * Stale-write guards: captures `_loadGeneration` at entry; re-checks state
 * after each await; bails if a new template loaded, the page is gone, or
 * the page was hydrated via another path.
 */
export async function hydratePage(pageId) {
  const projectGeneration = store.getEpoch() - store.getRevision();
  const key = `${projectGeneration}:${pageId}`;
  if (_inFlightHydrations.has(key)) return _inFlightHydrations.get(key);

  const myGeneration = _loadGeneration;
  const initial = store.getState().pages.find((page) => page.id === pageId);
  if (!initial) return { pageId, status: 'superseded' };
  if (!initial._pendingTemplateRefs?.length) return { pageId, status: 'hydrated' };
  const refs = initial._pendingTemplateRefs;
  const promise = (async () => {
    const index = await getSectionIndex();
    const lookups = {
      stitchLookup: buildStitchLookup(index),
      firstByCategory: buildFirstByCategory(index)
    };
    const fetched = await runWithConcurrency(refs, 8, async (ref) => {
      const data = await loadAndCache(ref.templateSiteName, ref.sectionId, ref.role, lookups);
      return { ref, data };
    });
    if (
      myGeneration !== _loadGeneration ||
      projectGeneration !== store.getEpoch() - store.getRevision()
    )
      return { pageId, status: 'superseded' };
    const fresh = store.getState();
    const idx = fresh.pages.findIndex((page) => page.id === pageId);
    if (idx < 0) return { pageId, status: 'superseded' };
    if (!fresh.pages[idx]._pendingTemplateRefs?.length) return { pageId, status: 'hydrated' };
    if (fresh.pages[idx]._pendingTemplateRefs !== refs) return { pageId, status: 'superseded' };
    const failedRefs = fetched.filter(({ data }) => !data).map(({ ref }) => ref.sectionId);
    // Keep the refs intact: dropping missing sections would turn a partial load
    // into a falsely complete audit (and erase the next attempt's work).
    if (failedRefs.length) return { pageId, status: 'failed', failedRefs };
    const instances = fetched.map(({ ref, data }) => {
      const inst = createInstance(data);
      inst.templateSiteName = ref.templateSiteName;
      inst.templateSectionId = ref.sectionId;
      return inst;
    });
    store.updateSilent(`pages.${idx}.sections`, instances);
    store.updateSilent(`pages.${idx}._pendingTemplateRefs`, null);
    return {
      pageId,
      status: fresh.pages[idx]._pendingTemplateRefs?.length ? 'failed' : 'hydrated'
    };
  })();
  _inFlightHydrations.set(key, promise);
  try {
    return await promise;
  } finally {
    if (_inFlightHydrations.get(key) === promise) _inFlightHydrations.delete(key);
  }
}

/**
 * Hydrate every page that still has pending refs. Used before save and
 * before export so serialized state is always materially complete.
 */
/**
 * Hydrate every ref-only page and wait for all of them.
 *
 * @param {{onProgress?: (progress: {done: number, total: number}) => void,
 *   signal?: AbortSignal|null, allowPartial?: boolean}} [opts]
 *   `onProgress` fires after each page settles, so a caller that blocks the user
 *   on this (the pre-publish audit gate) can show real counts instead of an
 *   indeterminate wait. It is never called when there is nothing to hydrate —
 *   callers use that to skip showing a progress surface at all.
 * @returns {Promise<{outcomes: object[], coverage: {pagesHydrated: number,
 *   pagesTotal: number, hydrationFailed: string[]}}>} Per-page failures are
 *   collected after all workers settle. The default rejects incomplete input
 *   with coverage/outcomes on the error; audit callers opt into allowPartial.
 *   Aborting only cancels this caller's wait.
 */
export async function ensureAllPagesHydrated({
  onProgress,
  signal = null,
  allowPartial = false
} = {}) {
  const abortError = () => new DOMException('Page hydration cancelled', 'AbortError');
  if (signal?.aborted) throw abortError();
  const pages = store.getState().pages;
  const pending = pages.filter((page) => page._pendingTemplateRefs?.length);
  const total = pending.length;
  const pagesTotal = pages.length;
  if (!total)
    return {
      outcomes: [],
      coverage: { pagesHydrated: pagesTotal, pagesTotal, hydrationFailed: [] }
    };
  let done = 0;
  const report = typeof onProgress === 'function' ? onProgress : null;
  report?.({ done, total });
  const hydration = runWithConcurrency(pending, 8, async (page) => {
    if (signal?.aborted) throw abortError();
    try {
      return await hydratePage(page.id);
    } catch (error) {
      return { pageId: page.id, status: 'failed', error };
    } finally {
      done++;
      if (!signal?.aborted) report?.({ done, total });
    }
  }).then((outcomes) => {
    const hydrationFailed = outcomes
      .filter((outcome) => outcome.status !== 'hydrated')
      .map((outcome) => outcome.pageId);
    const result = {
      outcomes,
      coverage: { pagesHydrated: pagesTotal - hydrationFailed.length, pagesTotal, hydrationFailed }
    };
    if (hydrationFailed.length && !allowPartial) {
      const error = new Error(`Could not load pages: ${hydrationFailed.join(', ')}`);
      error.coverage = result.coverage;
      error.outcomes = outcomes;
      throw error;
    }
    return result;
  });
  if (!signal) return hydration;
  // Shared navigation/prefetch loads may finish after this caller cancels.
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      signal.removeEventListener('abort', onAbort);
      reject(abortError());
    };
    signal.addEventListener('abort', onAbort, { once: true });
    if (signal.aborted) onAbort();
    hydration.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      }
    );
  });
}

/**
 * Kick off background hydration of every ref-only page. Concurrency 4
 * leaves capacity for a foreground hydratePage() (triggered by setActivePage)
 * to grab its own slot. Foreground priority is naturally promoted because
 * hydratePage() reuses an in-flight promise from _inFlightHydrations.
 *
 * Fires one `store.scheduleAutoSave()` on completion so a clean snapshot
 * of the fully-loaded template lands in IDB — guards against tab-close
 * crash recovery losing the template-load entirely (the per-page
 * `store.updateSilent` calls deliberately skip autosave).
 */
export function startBackgroundPrefetch() {
  stopBackgroundPrefetch();
  const myGeneration = _loadGeneration;
  const token = { aborted: false };
  _prefetchAbort = token;
  (async () => {
    const queue = store
      .getState()
      .pages.filter((p) => p._pendingTemplateRefs?.length)
      .map((p) => p.id);
    await runWithConcurrency(queue, 4, async (pageId) => {
      if (token.aborted || myGeneration !== _loadGeneration) return;
      try {
        await hydratePage(pageId);
      } catch (err) {
        console.warn('[SectionManager] background hydrate failed for', pageId, err);
      }
    });
    if (token.aborted || myGeneration !== _loadGeneration) return;
    if (typeof store.scheduleAutoSave === 'function') store.scheduleAutoSave();
  })();
}

export function stopBackgroundPrefetch({ invalidate = false } = {}) {
  if (_prefetchAbort) _prefetchAbort.aborted = true;
  _prefetchAbort = null;
  if (invalidate) _loadGeneration++;
}

/**
 * Load a template from the template catalog. Applies theme vars,
 * loads template CSS, and populates pages with template sections.
 * Uses a single transaction for atomic undo/redo.
 */
export async function loadTemplate(siteName, templateCatalog, { pageFilter = null } = {}) {
  let template = templateCatalog.find((t) => t.siteName === siteName);
  if (!template) {
    if (showToastFn) showToastFn(`Template "${siteName}" not found`, 'error');
    return;
  }

  // Slim picker-index entries carry no CSS blobs — hydrate the full payload on
  // selection (Phase 7 catalog split). Falls back to the slim entry if the
  // payload fetch fails: theme vars degrade gracefully, sections still load.
  if (template.slim) {
    try {
      template = await loadJSON(`data/template-payloads/${siteName}.json`);
    } catch (err) {
      console.warn(`[SectionManager] payload fetch failed for ${siteName}; using slim entry:`, err);
    }
  }

  // Cancel any prior template's background prefetch and advance the
  // generation token so in-flight hydrations for the previous template
  // detect a stale-write and bail.
  stopBackgroundPrefetch();
  _loadGeneration++;

  const state = store.getState();
  const sourceProfilePromise = loadTemplateSourceProfile(siteName, template);

  store.beginTransaction(`Load template "${siteName}"`);

  try {
    // 1. Theme — single coarse update, derived by the pure IF-2b function
    // (mappedTheme payloads build from a fresh canonical base; legacy
    // templates keep the prior-theme merge byte-identically — the contract and
    // its leak scenario are pinned in palette-mapping-tests.js).
    const cssThemeVars = extractRootThemeVars(
      `${template.rootCSS || ''}\n${template.darkCSS || ''}`
    );
    const { theme: newTheme, mergedThemeVars } = deriveTemplateLoadTheme({
      priorTheme: state.theme,
      template,
      cssThemeVars
    });
    store.update('theme', newTheme);

    // 2. Fonts — CSS-variable declarations are authoritative; clear stale
    // heading overrides when a newly-loaded template does not declare one.
    applyTemplateFonts(template, mergedThemeVars);

    // 3. Template CSS — kept as a local var purely for button-style inference
    // below. State no longer carries templateCSS; the renderer fetches it fresh
    // every render via assembleLiveTemplateCSS(state).
    await loadTemplateSupportCSS(siteName);

    // 4. Fetch page map
    const pageMap = await loadPageMap(siteName);
    // 4b. Momentum scrolling is part of a template's design when its source
    // ran Lenis (the crawl keeps the <html> class list per page). Derived on
    // every load, like the fonts: a template that did not declare it turns
    // it off again, and the Site Effects panel can overrule either way.
    store.update('siteEffects.smoothScroll', templateDeclaresSmoothScroll(pageMap), null);

    // 5. Load section data and build pages
    const index = await getSectionIndex();
    const lookups = {
      stitchLookup: buildStitchLookup(index),
      firstByCategory: buildFirstByCategory(index)
    };

    let newSharedSections = { navigation: null, footer: null };
    let newPages = [];

    if (pageMap && pageMap.pages && pageMap.pages.length > 1) {
      // ── Multi-page path (lazy) ──
      // pageFilter: when set, skip pages whose filename isn't in the set.
      // firstIncludedIdx drives which page gets eager hydration and which
      // becomes activePage — must be the first *included* page (Home may
      // have been excluded by the filter).
      const isIncluded = (entry) => !pageFilter || pageFilter.has(entry.filename);
      const firstIncludedIdx = pageMap.pages.findIndex(isIncluded);
      const firstIncluded =
        firstIncludedIdx >= 0 ? pageMap.pages[firstIncludedIdx] : pageMap.pages[0];

      // Shared nav/footer are needed on every page — load eagerly.
      const sharedEntries = ['navigation', 'footer']
        .map((role) => ({ role, sectionId: pageMap.shared?.[role] }))
        .filter((entry) => entry.sectionId);
      const sharedResultsPromise = runWithConcurrency(
        sharedEntries,
        8,
        async ({ role, sectionId }) => {
          const data = await loadAndCache(siteName, sectionId, role, lookups);
          return { role, sectionId, data };
        }
      );
      const firstPageResultsPromise = runWithConcurrency(
        firstIncluded.sections,
        8,
        async (sectionId) => {
          const data = await loadAndCache(siteName, sectionId, 'content', lookups);
          return { sectionId, data };
        }
      );
      const [sharedResults, firstPageResults] = await Promise.all([
        sharedResultsPromise,
        firstPageResultsPromise
      ]);

      for (const { role, sectionId, data } of sharedResults) {
        if (!data) continue;
        const inst = createInstance(data);
        inst.templateSiteName = siteName;
        inst.templateSectionId = sectionId;
        newSharedSections[role] = inst;
      }

      // First included page: hydrate eagerly so first render shows real content.
      // Remaining included pages: store as lightweight refs; hydration happens
      // lazily via hydratePage() on page-switch and via background prefetch.
      for (let i = 0; i < pageMap.pages.length; i++) {
        const pageEntry = pageMap.pages[i];
        if (!isIncluded(pageEntry)) continue;
        const isFirst = i === firstIncludedIdx;
        const page = createTemplatePage(
          pageEntry.name,
          pageEntry.sourceTemplateDocument,
          pageEntry.sourceTemplateSectionPlacement
        );
        if (isFirst) {
          for (const { sectionId, data } of firstPageResults) {
            if (!data) continue;
            const inst = createInstance(data);
            inst.templateSiteName = siteName;
            inst.templateSectionId = sectionId;
            page.sections.push(inst);
          }
        } else {
          page._pendingTemplateRefs = pageEntry.sections.map((sectionId) => ({
            sectionId,
            role: 'content',
            templateSiteName: siteName
          }));
        }
        newPages.push(page);
      }
    } else {
      // ── Single-page fallback ──
      const idOccurrence = new Map();
      const plan = template.sections.map((templateSection) => {
        const id = templateSection.id;
        const count = idOccurrence.get(id) || 0;
        idOccurrence.set(id, count + 1);
        const filename = count === 0 ? id : `${id}--${count}`;
        return { templateSection, filename };
      });

      const fetched = await runWithConcurrency(plan, 8, async ({ templateSection, filename }) => {
        let data = await loadTemplateSectionData(siteName, filename);
        if (!data) {
          const match = findBestCatalogMatch(templateSection, lookups);
          if (match) data = await loadSectionData(match.catalogKey, match.category);
        }
        return { templateSection, data };
      });

      const page = createTemplatePage('Home', template.sourceTemplateDocument);
      for (const { templateSection, data } of fetched) {
        if (!data) continue;

        if (isSharedRole(templateSection.role)) {
          const inst = createInstance(data);
          inst.templateSiteName = siteName;
          inst.templateSectionId = templateSection.id;
          newSharedSections[templateSection.role] = inst;
          continue;
        }

        const inst = createInstance(data);
        inst.templateSiteName = siteName;
        inst.templateSectionId = templateSection.id;
        page.sections.push(inst);
      }

      newPages.push(page);
    }

    // Guard: ensure at least one page
    if (newPages.length === 0) {
      newPages.push(createTemplatePage('Home'));
    }

    // 6. Template CSS already carries the source site's .cs-button-solid rules.
    // Do not synthesize a later global override from the first section variant,
    // or section-specific buttons can accidentally become the whole-site style.
    const allSections = newPages.flatMap((p) => p.sections);
    if (newSharedSections.navigation) allSections.unshift(newSharedSections.navigation);
    if (newSharedSections.footer) allSections.push(newSharedSections.footer);
    const sourceProfile = await sourceProfilePromise;
    const clientInfo = deriveClientInfoFromTemplate({
      siteName,
      sourceProfile,
      sections: allSections,
      projectName: state.projectName
    });
    // Only the template's bubbly-hover effect is captured (origin:'auto'); no
    // root-rule colors/padding are synthesized, so the template's own button CSS
    // is never stripped and section buttons never become the whole-site style.
    store.update('buttonStyles', detectTemplateButtonEffect(allSections));
    store.update('clientInfo', clientInfo);
    if (sourceProfile && typeof sourceProfile === 'object') {
      store.update('sourceClientProfile', clientInfoToSourceProfile(clientInfo, sourceProfile));
    }

    // 7. Apply coarse state replacements
    store.update('sharedSections', newSharedSections);
    store.update('pages', newPages);
    store.update('activePage', newPages[0].id);

    // Track the source template so the 11ty exporter can bundle it as
    // _reference/original-template/ for LLM-assisted edits post-export.
    store.update('sourceTemplateDomain', siteName);

    store.commitTransaction();
  } catch (err) {
    store.abortTransaction();
    throw err;
  }

  const totalSections = store
    .getState()
    .pages.reduce((sum, p) => sum + p.sections.length + (p._pendingTemplateRefs?.length || 0), 0);
  bridge.clearScroll();
  await renderPreviewFn({ preserveScroll: false, transition: null, hideDuringLoad: false });
  if (showToastFn)
    showToastFn(
      `Loaded "${siteName}" template (${totalSections} sections, ${store.getState().pages.length} pages)`,
      'success'
    );

  // Nudge user to fill in client details if template uses client link bindings
  if (showToastFn) {
    const state = store.getState();
    const allHTML =
      state.pages.flatMap((p) => p.sections.map((s) => s.html)).join('') +
      Object.values(state.sharedSections || {})
        .map((s) => s?.html || '')
        .join('');
    if (allHTML.includes(CLIENT_LINK_MARKER_PREFIX)) {
      setTimeout(() => {
        showToastFn(
          'Template uses client data bindings \u2014 fill in your Client details in the sidebar',
          {
            type: 'info',
            duration: 6000,
            action: {
              label: 'Open Client Panel',
              onClick: () => eventBus.emit('sidebar:show-tab', 'client')
            }
          }
        );
      }, 1500);
    }
  }
}

/**
 * Append a new page to the current project, populated with the chosen
 * page's sections from another template. Does not touch sharedSections,
 * theme, fonts, button styles, or sourceTemplateDomain — only the new
 * page is added.
 *
 * Returns `{ pageId, firstInstanceId }` on success, or `undefined` on
 * a missing page-map or missing page entry (with a toast).
 */
export async function addTemplatePage({ siteName, filename }) {
  const pageMap = await loadPageMap(siteName);
  if (!pageMap || !Array.isArray(pageMap.pages)) {
    if (showToastFn) showToastFn(`Template "${siteName}" has no page map`, 'error');
    return;
  }
  const entry = pageMap.pages.find((p) => p.filename === filename);
  if (!entry) {
    if (showToastFn) showToastFn(`Page "${filename}" not found in template "${siteName}"`, 'error');
    return;
  }

  const index = await getSectionIndex();
  const lookups = {
    stitchLookup: buildStitchLookup(index),
    firstByCategory: buildFirstByCategory(index)
  };

  const sectionIds = Array.isArray(entry.sections) ? entry.sections : [];
  const fetched = await runWithConcurrency(sectionIds, 8, async (sectionId) => ({
    sectionId,
    data: await loadAndCache(siteName, sectionId, 'content', lookups)
  }));

  const state = store.getState();
  const uniqueName = getUniquePageName(state, entry.name || 'Page');
  const page = createTemplatePage(uniqueName);
  for (const { sectionId, data } of fetched) {
    if (!data) continue;
    const inst = createInstance(data, { matchCurrentButtonMarkup: true });
    inst.templateSiteName = siteName;
    inst.templateSectionId = sectionId;
    page.sections.push(inst);
  }

  store.beginTransaction(`Add page "${uniqueName}" from ${siteName}`);
  try {
    const nextPages = [...state.pages, page];
    store.update('pages', nextPages, null);
    store.update('activePage', page.id, null);
    store.commitTransaction();
  } catch (err) {
    store.abortTransaction();
    throw err;
  }

  bridge.clearScroll();
  await renderPreviewFn({
    preserveScroll: false,
    transition: 'page-switch',
    hideDuringLoad: false
  });
  if (showToastFn) showToastFn(`Added page "${uniqueName}" from ${siteName}`, 'success');

  return { pageId: page.id, firstInstanceId: page.sections[0]?.instanceId || null };
}

// Button-style extraction moved to shared/button-style-extract.js. The old
// first-section-colors-become-site-wide extractor (extractButtonStyles) is now
// migration-only; the template-load path uses detectTemplateButtonEffect (bubbly
// sniff only). Re-exported for the button-style test suite + migration callers.
export { extractButtonStyles, detectTemplateButtonEffect };

/** Build a Map of stitchNumber → SectionIndexEntry[] for fast lookup. */
function buildStitchLookup(index) {
  const map = new Map();
  for (const entry of index) {
    const num = entry.stitchNumber;
    if (typeof num !== 'number') continue;
    if (!map.has(num)) map.set(num, []);
    map.get(num).push(entry);
  }
  return map;
}

/** Build a Map of category → first SectionIndexEntry for category fallback. */
function buildFirstByCategory(index) {
  const map = new Map();
  for (const entry of index) {
    if (!map.has(entry.category)) map.set(entry.category, entry);
  }
  return map;
}

/**
 * Find the best catalog section match for a template section.
 * Tries stitch-number matching first, then falls back to category inference.
 */
function findBestCatalogMatch(templateSection, lookups) {
  const sectionId = templateSection.id || '';
  const role = templateSection.role || 'content';
  const { stitchLookup, firstByCategory } = lookups;

  // Step 1: stitch-number match (primary)
  const numMatch = sectionId.match(/-(\d{2,})$/);
  if (numMatch) {
    const num = parseInt(numMatch[1], 10);
    const candidates = stitchLookup.get(num) || [];
    console.debug(
      `[TemplateMatch] "${sectionId}" → stitch ${num}, ${candidates.length} candidate(s):`,
      candidates.map((c) => c.sectionId)
    );

    if (candidates.length === 1) return candidates[0];

    if (candidates.length > 1) {
      const byExactId = candidates.find((s) => s.sectionId === sectionId);
      if (byExactId) return byExactId;

      const inferred = inferCategory(sectionId, role);
      if (inferred) {
        const byCategory = candidates.find((s) => s.category === inferred);
        if (byCategory) return byCategory;
      }

      if (isSharedRole(role)) {
        const byRole = candidates.find((s) => s.category === role);
        if (byRole) return byRole;
      }

      console.warn(
        `[TemplateMatch] Ambiguous stitch ${num} for section "${sectionId}"; using category fallback.`
      );
    }

    if (candidates.length === 0) {
      console.warn(
        `[TemplateMatch] Stitch ${num} from "${sectionId}" not found in index; using category fallback.`
      );
    }
  }

  // Step 2: category fallback
  const category = inferCategory(sectionId, role);
  if (!category) {
    console.warn(`[TemplateMatch] "${sectionId}" → no category inferred, skipping`);
    return null;
  }
  const fallback = firstByCategory.get(category) || null;
  console.debug(
    `[TemplateMatch] "${sectionId}" → category fallback "${category}" → ${fallback ? fallback.sectionId : 'null'}`
  );
  return fallback;
}

/**
 * Get sections in the same category as a given section, for swap UI.
 */
export async function getCategorySections(category, options = {}) {
  const { sharedType = null } = options;
  const index = await getSectionIndex();
  const effectiveCategory = sharedType || category;
  let variants = index.filter((s) => s.category === effectiveCategory);

  // Legacy / imported sections can end up with category "unknown" (or missing).
  // In that case, allow swapping by surfacing a broad catalog fallback instead
  // of returning an empty list that blocks the swap UI.
  if (!sharedType && (variants.length === 0 || isUnknownCategory(effectiveCategory))) {
    variants = index.filter((s) => s.category !== 'navigation');
  }

  if (sharedType === 'footer') {
    return variants.filter((s) => !isSplitFooterSectionVariant(s));
  }
  return variants;
}
